import { cn } from '@invent/wl-ui-kit-next';
import { Button, Checkbox } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';

import {
  asSpelled,
  CompactBar,
  CompactChipLine,
  CompactDivider,
  CompactFilters,
  CompactMenu,
  CompactSearch,
  CompactTitle,
  FilterBox,
  FilterChip,
  HelpPopover,
  NotPermitted,
  PageHeader,
  SelectChip,
  SplitList,
  STICKY_BAR_TOP,
  STICKY_TABLE_TOP,
  useScrolledPast,
  useWorkToolbar,
  WorkToolbar,
  type DataTableState,
  type ICompactChip,
  type ICompactMenuItem,
  type IDataColumn
} from '@/design';
import { useCan } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import { brokerCheckRefreshApi } from '../../api/brokercheck-refresh-api';
import type { IRefreshRunDetail } from '../../api/brokercheck-refresh-contracts';
import type { IPage } from '../../api/oversight-contracts';
import type {
  IWorkItemAssignee,
  IWorkItemCounts,
  IWorkItemCountsQuery,
  IWorkItemQuery,
  IWorkItemRow
} from '../../api/work-item-contracts';
import { workItemsApi } from '../../api/work-items-api';
import {
  LoadError,
  PastTheEnd,
  pageOf,
  toggleRepeated,
  useAdminData,
  withUnknown
} from '../../components/admin-page';
import { ListArea, usePaneRead } from '../../components/list-area';
import { useLastVisit } from '../../components/my-tasks-summary';
import type { ICardLinkState } from '../../components/participant-link';
import {
  SignalChips,
  StateCards,
  type IStateSignal,
  type SignalSpec
} from '../../components/state-cards';
import { TEAM_PATHS, refreshRunPath, workItemPath } from '../../paths';
import { refusalMessage } from '../../refusal';
import {
  FIRM_MOVE_TRIGGER,
  WORK_ITEM_QUEUES,
  WORK_ITEM_TRIGGERS,
  isWorkItemQueue,
  triggerLabel
} from '../../work-items';
import { useChosenCodes, useOwnColumns } from '../lists/work-page/columns';
import { ParamChip } from '../sec-adv/firm-filters';
import { WorkPageHeaderActions, useReadAt } from '../lists/work-page/header-actions';
import {
  WORK_ITEMS_MAX_PAGE,
  WorkPager,
  dropPaging,
  readItemPages,
  usePerPage,
  useWorkPaging,
  windowOf
} from '../lists/work-page/pager';
import {
  ALL_QUEUES_ANSWERS,
  answerColumn,
  isAnswerCode,
  readAnswerColumns,
  type IAnswerColumns
} from './answer-columns';
import {
  ASSIGN_LIMIT,
  AssignAllItems,
  AssignedSaid,
  SelectedAssign,
  matchOf,
  useAssignMissing,
  type IAssignedSaid
} from './bulk-assign';
import { BulkDecisions, useQuerySelection } from './bulk-decisions';
import { FunnelChoice, FunnelHeader, ParamFunnel, SortHeader } from './queue-headers';
import { useQueueSearch } from './queue-search';
import {
  ALL_STAFF_FIELDS_NOTE,
  readStaffColumns,
  readStemColumns,
  STEM_CODES,
  type IStaffColumns,
  type IStemColumn
} from './staff-columns';
import { DISCLOSURES_NOTE, WorkItemStateChip, WorkItemsTable, workItemColumns } from './work-items-table';

/**
 * The compliance queues (SR-222, F9's S11; CMP-09): survey-api's work items, one tab per queue,
 * the item opened beside the list (`AdminComplianceArea`) — in the work pages' layout (SR-485):
 * the queue tabs, the views as cards, one toolbar, the selection bar, the table's headers sorting
 * and filtering. The item is the record, because a participant can hold several (the work-items
 * design's §9).
 *
 * **The URL is the state**, as on every Team Portal list: `survey` (the tab; absent is All),
 * `view`, `trigger` and `criterion` (repeated), `assignee`, `flag`, `sort`, `run` and `page`. A value no control stands for is
 * still sent and drawn as spelled — survey-api's 400 `invalid-query` then names it, in its words
 * (`refusalMessage`) — and a page is drawn only under the query it answers. `view` is the one
 * parameter that is this page's own rather than survey-api's: the design's segments, sent as the
 * states they stand for (F9's R2), so a view this page does not know reads as the default.
 *
 * **The counts are one read** (`/work-items/counts`, under the list's filters but the state and
 * the flag): each view's card counts its states, with their overdue, due today, new, unassigned
 * and own items as the card's signals — each a toggle that narrows the view by it too. The first
 * three are a `flag`, repeated: survey-api keeps the items raising any of them, so the counts stay
 * whole, and the chips line says them joined by "or". The last two are the Assigned to filter's
 * positions (`assignee`), said by that filter and ANDed like it; a signal of another view opens
 * it. "New"
 * is since the viewer's last visit to My tasks (`useLastVisit`, read and never written here), and
 * not shown before one. Each tab counts its queue's items in the view, survey-api counting the
 * queues without the survey filter. The tabs are `WORK_ITEM_QUEUES`, so a tab never appears with
 * a queue's first item and vanishes with its last: `0` where the read is silent, nothing while it
 * is out or failed, never a `0` it did not say — and a queue only the read or the URL names gets a
 * tab of its own, spelled as sent. The counts are read again with the list, never polled.
 *
 * **The columns are the viewer's** (`useOwnColumns`, one Columns sheet, kept per tab, since each
 * tab offers different ones): a survey tab adds its staff fields (F9's Task 8,
 * `staff-columns.tsx`) — the list waits for the survey's staff form, read once per page load, and
 * asks every field it offers by `fields=`; a form that cannot be read leaves the queue without
 * them, said above the table. A survey tab also offers its survey questions (SR-398,
 * `answer-columns.tsx`), as Lists' catalogue names them, read once per survey per page load; only
 * the chosen ones are asked, so a choice reads the list again. All offers no staff field, and its
 * sheet says so, and one question, every queue's U4 explanation. All offers Quality Rating and
 * Travel, each row's of its own survey (`readStemColumns`). Subject, Raised, Last modified and Due
 * sort by survey-api's four orders; Raised by, Assigned to and — on All — State filter, and
 * Concern narrows to the items the chosen concern criteria raised (SR-410), hidden from a reader
 * the criteria's read refuses.
 *
 * **Left firm is a view** (`view=left_firm`, SR-468): the firm changes still to work — the people
 * who moved firm, `movedFirm=true` over the unclosed states — on any tab, Raised by not offered
 * there and its `trigger` dropped on the way in (`toView`). Outstanding, Need info or escalated and
 * Escalated leave them out (`movedFirm=false`); Reviewed and All keep every item. The counts read
 * is one, sent without `movedFirm`: those three cards and their tabs count its `states` and
 * `bySurvey` less `firmMoves` and `firmMovesBySurvey`, Left firm counts the firm moves alone ("…"
 * where the read sent none). An address of the Left firm tab this replaced (`queue=left_firm`)
 * opens All on the Left firm view.
 *
 * **`run` narrows to the items one BrokerCheck refresh run raised** (F10's Q3, B3), sent as
 * survey-api's `refreshRunId` — the run's record links here, whole and per trigger. Its chip names
 * the run by when it was created, read once for an account holding `integrations.configure`;
 * survey-api refuses the run's route to any other, so theirs says one run raised them and names
 * none. An id that is not a GUID is still sent, and survey-api's 400 says it in its words.
 *
 * **Rows can be ticked for one decision over them all** (`BulkDecisions`, F9's R7): the selection
 * is this query's, so a change of tab, view, filter or page clears it; a decision that lands reads
 * the list and the counts again. The bar also assigns the ticked items, and *Assign all N…* every
 * item the query matches (`bulk-assign.tsx`, SR-466).
 *
 * **`q` searches** the participant's name and firm, the flagged member's and the CRDs (SR-466), read as
 * typed; it narrows the cards and the tabs as any filter, so a search shows its hits per queue.
 *
 * **The page scrolls, the table's header sticking** under the app bar: once the cards and the
 * filters have scrolled away, the compact bar (`condense.tsx`) draws them in the app bar's row — the
 * queue tabs and the open view as menus, the search, *Filters*, *Assign all* and *Columns* — and
 * the filters applied on a row under it, as the page draws them under its toolbar; the selection
 * bar sticks under both. An item open beside the list draws the list as its pane instead, with no
 * bar.
 */
export function AdminCompliancePage() {
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const survey = params.get('survey');
  const view = viewOfParams(params);
  const leftFirm = view === 'left_firm';
  // The Left firm tab's old address: All, on the Left firm view.
  React.useEffect(() => {
    if (params.get('queue') !== LEFT_FIRM) return;
    const next = new URLSearchParams(params);
    next.delete('queue');
    next.delete('survey');
    toView(next, LEFT_FIRM);
    setParams(next, { replace: true });
  }, [params, setParams]);
  const triggers = params.getAll('trigger');
  const criteriaChosen = params.getAll('criterion');
  // An empty `assignee`, `sort` or `run` is no filter: neither sent (the client drops an empty
  // value) nor drawn.
  const assignee = params.get('assignee') || null;
  const sort = params.get('sort') || null;
  const run = params.get('run') || null;
  const flags = flagsOf(params);
  const q = params.get('q') ?? '';
  const { assignees, assigneesUnread } = useAssignees();
  const criteria = useCriteria();
  const assignMissing = useAssignMissing();
  const assignReasonId = React.useId();
  // Keyed by the press, so a new assignment's line starts with its own Undo.
  const [assignedSaid, setAssignedSaid] = React.useState<Nullable<{ said: IAssignedSaid; n: number }>>(null);
  const { since } = useLastVisit();

  // The window — the page and the rows *Load more* added — is the URL's.
  const paging = useWorkPaging();
  const { perPage, pages } = paging;
  const query = complianceQueryOf(params, since, undefined, perPage);
  const asked = JSON.stringify(query);
  const columnsId = survey === null ? 'compliance' : `compliance.${survey}`;
  // The order of the chosen questions is the columns', not the read's: moving one reads nothing.
  const answerCodes = [...new Set(useChosenCodes(columnsId).filter(isAnswerCode))].sort();
  const readKey = `${asked}|${pages}|${answerCodes.join(' ')}`;
  // One catalogue read per survey per page load, shared by the sheet and the rows read; a failed one
  // is read again next time.
  const catalogues = React.useRef(new Map<string, Promise<IAnswerColumns>>());
  const answersOf = (code: Nullable<string>): Promise<IAnswerColumns> => {
    if (code === null) return Promise.resolve(ALL_QUEUES_ANSWERS);
    const held = catalogues.current.get(code);
    if (held) return held;
    const read = readAnswerColumns(code).then((answers) => {
      if (answers.error !== null) catalogues.current.delete(code);
      return answers;
    });
    catalogues.current.set(code, read);
    return read;
  };
  const questions = useAdminData<{ survey: Nullable<string>; answers: IAnswerColumns }>(
    async () => ({ survey, answers: await answersOf(survey) }),
    [survey]
  );
  const { data, error, reload, reading } = useAdminData<{
    asked: string;
    page: IPage<IWorkItemRow>;
    survey: Nullable<string>;
    columns: string;
    staff: Nullable<IStaffColumns>;
    stems: IStemColumn[];
  }>(
    async (signal) => {
      // The rows wait for the catalogue only when a question is asked, to send the ones it holds.
      const [staff, stems, answers] = await Promise.all([
        survey === null ? null : readStaffColumns(survey),
        survey === null ? readStemColumns(WORK_ITEM_QUEUES) : [],
        answerCodes.length === 0 ? null : answersOf(survey)
      ]);
      try {
        const fields = [
          ...(staff?.fields.map((field) => field.code) ?? []),
          ...(survey === null ? STEM_CODES : []),
          ...answerCodes.filter((code) => answers?.fields.has(code))
        ];
        const page = await readItemPages(
          (at, pageSize) =>
            workItemsApi.listWorkItems({ ...query, ...(fields.length ? { fields } : {}), page: at, pageSize }, signal),
          query.page ?? 1,
          perPage,
          pages,
          WORK_ITEMS_MAX_PAGE
        );
        return { asked: readKey, page, survey, columns: columnsId, staff, stems };
      } catch (listError) {
        throw new Error(refusalMessage(listError, 'Could not load this page'));
      }
    },
    [readKey]
  );
  // The last answer while the next is out; nothing once this query's read has failed.
  const shown = error && data?.asked !== readKey ? null : data;
  const countsQuery = countsQueryOf(params, query, since);
  const counts = useAdminData<IWorkItemCounts>(
    (signal) => workItemsApi.workItemCounts(countsQuery, signal),
    [JSON.stringify(countsQuery)]
  );
  // A count survey-api did not just say is not drawn: a failed re-read leaves none.
  const counted = counts.error ? null : counts.data;
  // The header's Refresh reads the rows and the counts again; "updated" is the rows' last answer.
  const readAt = useReadAt(data);
  const readAgain = () => {
    void reload();
    void counts.reload();
    if (questions.error || questions.data?.answers.error) void questions.reload();
  };
  const selection = useQuerySelection(asked, shown?.page.items ?? [], (row) => row.id);

  /** A filter change starts at page 1: page 3 of the old query means nothing. */
  const update = (change: (next: URLSearchParams) => void) => {
    const next = new URLSearchParams(params);
    change(next);
    setParams(dropPaging(next));
  };
  const choose = (name: string) => (value: Nullable<string>) =>
    update((next) => {
      next.delete(name);
      if (value !== null) next.set(name, value);
    });
  // Replacing, so typing leaves no history entry per word.
  const searchFor = (text: string) =>
    setParams(
      (current) => {
        const next = dropPaging(new URLSearchParams(current));
        if (text === '') next.delete('q');
        else next.set('q', text);
        return next;
      },
      { replace: true }
    );
  const searchBox = {
    label: 'Filter work items',
    placeholder: 'Filter work items…',
    ...useQueueSearch(q, searchFor)
  };
  const onAssigned = (said: IAssignedSaid) => {
    setAssignedSaid((last) => ({ said, n: (last?.n ?? 0) + 1 }));
    if (!said.refused) selection.onSelect([]);
    readAgain();
  };
  const assignAll = (
    <AssignAllItems
      match={matchOf(query)}
      // The count of the query the filter sends, never the last answer's while the next is out.
      count={shown?.asked === readKey ? shown.page.total : null}
      assignees={assignees}
      missing={assignMissing}
      reasonId={assignReasonId}
      onAssigned={onAssigned}
    />
  );
  const toggleTrigger = (trigger: string, on: boolean) =>
    update((next) => toggleRepeated(next, 'trigger', WORK_ITEM_TRIGGERS, trigger, on));
  const criterionCodes = criteria.codes;
  const toggleCriterion = (code: string, on: boolean) =>
    update((next) => toggleRepeated(next, 'criterion', criterionCodes, code, on));
  const turnTo = paging.setPage;

  // The tab, the view and the order are where the reader is, not filters: Clear all keeps them.
  const clear = () => {
    const next = new URLSearchParams();
    for (const name of ['survey', 'view', 'sort']) {
      const value = params.get(name);
      if (value !== null) next.set(name, value);
    }
    setParams(next);
  };
  const tableState: DataTableState | undefined =
    shown === null
      ? error
        ? { kind: 'error', content: <LoadError message={error} /> }
        : { kind: 'loading' }
      : shown.page.items.length === 0
        ? {
            kind: 'empty',
            content:
              shown.page.total > 0 ? (
                <PastTheEnd
                  page={shown.page.page}
                  pageSize={shown.page.pageSize}
                  total={shown.page.total}
                  counted={['item', 'items']}
                  onPage={turnTo}
                />
              ) : (
                'No work item matches this view.'
              )
          }
        : undefined;

  const triggerSaid = triggers.map((trigger) => asSpelled(triggerLabel(trigger))).join(', ');
  const triggerBoxes = (
    <fieldset aria-label="Raised by" className="grid gap-2">
      {withUnknown(WORK_ITEM_TRIGGERS, triggers).map((trigger) => (
        <label key={trigger} className="flex items-center gap-2">
          <Checkbox
            checked={triggers.includes(trigger)}
            onCheckedChange={(next) => toggleTrigger(trigger, next === true)}
          />
          {asSpelled(triggerLabel(trigger))}
        </label>
      ))}
    </fieldset>
  );
  // A code the read does not name — or before it lands — is said as spelled.
  const criterionName = (code: string) => criteria.names.get(code) ?? asSpelled(code);
  const criterionSaid = criteriaChosen.map(criterionName).join(', ');
  const criterionBoxes = (
    <fieldset aria-label="Concern" className="grid max-h-72 gap-2 overflow-y-auto">
      {withUnknown(criterionCodes, criteriaChosen).map((code) => (
        <label key={code} data-criterion-option={code} className="flex items-center gap-2">
          <Checkbox
            checked={criteriaChosen.includes(code)}
            onCheckedChange={(next) => toggleCriterion(code, next === true)}
          />
          <span className="wrap-anywhere">{criterionName(code)}</span>
        </label>
      ))}
    </fieldset>
  );
  const assigneeOptions = [
    { value: 'me', label: 'Me' },
    { value: 'none', label: 'Unassigned' },
    ...assignees.map((account) => ({ value: account.accountId, label: account.email }))
  ];
  const assigneeSaid =
    assignee === null
      ? null
      : (assigneeOptions.find((option) => option.value === assignee)?.label ?? asSpelled(assignee));

  /** A header that sorts by one of survey-api's orders; `raised_at` is the default, never written. */
  const sortHead = (order: QueueSort, funnel?: React.ReactNode) => {
    const { label, direction, note } = SORTS[order];
    const active = order === 'raised_at' ? sort === null || sort === order : sort === order;
    return {
      label,
      header: (
        <SortHeader
          label={label}
          direction={direction}
          active={active}
          note={note}
          onPress={() => choose('sort')(order === 'raised_at' || active ? null : order)}
          funnel={funnel}
        />
      )
    };
  };
  const heads: Record<string, Pick<IDataColumn<IWorkItemRow>, 'label' | 'header'>> = {
    // Left firm is the firm moves alone: Raised by is not offered there.
    ...(!leftFirm && {
      trigger: {
        label: 'Raised by',
        header: (
          <FunnelHeader
            label="Raised by"
            funnel={
              <ParamFunnel code="trigger" label="Raised by" said={triggerSaid || null}>
                {triggerBoxes}
              </ParamFunnel>
            }
          />
        )
      }
    }),
    subject: sortHead('subject'),
    raised: sortHead('raised_at'),
    due: sortHead('due_on'),
    updated: sortHead('updated_at'),
    assignee: {
      label: 'Assigned to',
      header: (
        <FunnelHeader
          label="Assigned to"
          funnel={
            <ParamFunnel code="assignee" label="Assigned to" said={assigneeSaid}>
              <FunnelChoice
                label="Assigned to"
                value={assignee}
                options={[
                  { value: null, label: 'Anyone' },
                  ...assigneeOptions,
                  // One no option stands for, as the URL named it.
                  ...(assignee !== null && !assigneeOptions.some((option) => option.value === assignee)
                    ? [{ value: assignee, label: asSpelled(assignee) }]
                    : [])
                ]}
                onChoose={choose('assignee')}
              />
            </ParamFunnel>
          }
        />
      )
    },
    // Every state is listed on All alone: another view is its states already.
    ...(view === 'all'
      ? {
          state: {
            label: 'State',
            header: (
              <FunnelHeader
                label="State"
                funnel={
                  <ParamFunnel code="state" label="State" said={null}>
                    <p className="text-muted-foreground">Show only the items in one view:</p>
                    <FunnelChoice
                      label="State"
                      value="all"
                      options={VIEWS.map((entry) => ({ value: entry, label: VIEW_LABEL[entry] }))}
                      onChoose={(value) => update((next) => toView(next, value ?? 'outstanding'))}
                    />
                  </ParamFunnel>
                }
              />
            )
          }
        }
      : {})
  };
  const own = workItemColumns({
    search: location.search,
    itemPath: (id) => workItemPath(id),
    queueColumn: survey === null,
    staffFields: shown?.staff?.fields,
    stemFields: shown?.stems
  }).map((column) => (heads[column.id] ? { ...column, ...heads[column.id] } : column));
  // The questions of the survey the rows are of: none while its catalogue is out.
  const offered = shown && questions.data?.survey === shown.survey ? questions.data.answers : null;
  const chosen = useOwnColumns<IWorkItemRow>(shown?.columns ?? 'compliance', {
    catalogue: offered?.catalogue ?? null,
    offers: (code) => offered?.fields.has(code) ?? isAnswerCode(code),
    column: (code) => answerColumn(offered, code) ?? { id: code, header: '', label: code, cell: () => null }
  });
  // The header counts every view — All's card — under the filters, not the open view's rows.
  const total = counted ? viewCount(counted, 'all', 'count', params) : null;
  // Where the page's own controls end: scrolled under the app bar, the compact bar stands for them.
  const [controlsEnd, setControlsEnd] = React.useState<Nullable<HTMLElement>>(null);
  const scrolled = useScrolledPast(controlsEnd);

  // The controls drawn twice — in the page, and slimmed in the compact bar once they scroll away.
  const refresh = { reading: reading || counts.reading, readAt, onClick: readAgain };
  const help = (
    <HelpPopover label="About Compliance queues" align="end">
      <p>
        The work items BrokerCheck changes, concern criteria and staff raise, one tab
        per survey queue. Open an item beside the list to read it and decide, or tick
        several for one decision over them all — offered only where every ticked item takes
        it: a reviewed item takes none, a firm move only its own.
      </p>
      <p>
        Remove needs a rationale; Approve, Escalate, Need additional info, Remove just this advisor,
        Left firm and Approved don’t. Remove just this advisor takes one member off a team or firm
        and leaves its standing as it is.
      </p>
      <p>
        Left firm holds the firm changes still to work — the people who moved firm — decided Left
        firm or Approved; Outstanding, Need info or escalated and Escalated leave them out, and
        Reviewed and All keep them. A CRD changed alone is decided like any other item.
      </p>
      <p>Pending / final: {DISCLOSURES_NOTE}.</p>
      <p>Each card is a view of the items by state:</p>
      <ul className="grid list-disc gap-1 pl-5">
        {VIEWS.map((entry) => (
          <li key={entry}>
            <span className="font-medium">{VIEW_LABEL[entry]}</span> — {VIEW_ABOUT[entry]}
          </li>
        ))}
      </ul>
      <p>
        A card’s badges count the items in it that are overdue, due today, new since your
        last visit to My tasks, unassigned or assigned to you. Press a badge to show only
        those items: overdue, due today and new show the items of any one pressed, and keep
        the counts as they are; unassigned and yours set Assigned to. Press it again, or its
        ✕, to drop it. Hover a card for what it holds.
      </p>
      <p>
        Columns adds a queue’s survey questions: each row shows the answer from the flagged
        person’s newest survey on that queue, blank where there is none, marked “draft” while the
        survey is not submitted. On Teams and RIA firms a “Flagged member” column shows the answer
        of the member the item is about; All offers the U4 explanation of every queue.
      </p>
      <p>
        Scrolled down, the header, the cards and the filters ride slimmed in the bar at the top of
        the window, with the table’s column names under them.
      </p>
    </HelpPopover>
  );
  const viewCards = <ViewCards params={params} view={view} counts={counted} />;
  // The flags pressed, any of them; an assignee's signal is said by Assigned to, ANDed with them.
  const flagChip = (
    <SignalChips
      chips={flags.map((flag) => {
        const words = FLAG_WORDS[flag] ?? asSpelled(flag);
        return {
          key: flag,
          words,
          clearName: `Show every item, not only ${words}`,
          onClear: () => update((next) => withoutValue(next, 'flag', flag))
        };
      })}
    />
  );
  const raisedBy = (
    <FilterChip label="Raised by" summary={triggerSaid}>
      {triggerBoxes}
    </FilterChip>
  );
  // Offered where the criteria could be read, or where the URL already narrows by one.
  const concern = (criteria.readable || criteriaChosen.length > 0) && (
    <FilterChip label="Concern" summary={criterionSaid}>
      {criterionBoxes}
    </FilterChip>
  );
  const assignedTo = (
    <SelectChip
      label="Assigned to"
      any="Anyone"
      value={assignee}
      options={assigneeOptions}
      onChoose={choose('assignee')}
    />
  );
  const columnsButton = chosen.button(own, survey === null ? ALL_STAFF_FIELDS_NOTE : '');
  const runRead = useRunRead(run);
  const runChip = run !== null && (
    <RunFilterChip runId={run} read={runRead} onRemove={() => choose('run')(null)} />
  );
  // The toolbar's quick filters, drawn in the page and again in the compact bar's *Filters*.
  const quickFilters = (
    <>
      {!leftFirm && raisedBy}
      {concern}
      {assignedTo}
      {runChip}
    </>
  );
  // Every filter applied, as a chip with its value — under the toolbar and on the compact bar's
  // second row: the flags pressed first ("Showing: …"), then each quick filter set, whose press
  // opens its control (`open`, by its name) and whose ✕ clears it.
  const toolbar = useWorkToolbar();
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const openFilters = () => {
    setFiltersOpen(true);
    return true as const;
  };
  const quickChipsWith = (open: (label: string) => true): ICompactChip[] => {
    const said: [string, string, Nullable<string>, () => void][] = [
      [
        'trigger',
        'Raised by',
        leftFirm ? null : triggerSaid || null,
        () => update((next) => next.delete('trigger'))
      ],
      ['criterion', 'Concern', criterionSaid || null, () => update((next) => next.delete('criterion'))],
      ['assignee', 'Assigned to', assigneeSaid, () => choose('assignee')(null)],
      ['run', 'Refresh run', run === null ? null : runRead.summary, () => choose('run')(null)]
    ];
    return said.flatMap(([key, label, value, onRemove]) =>
      value === null
        ? []
        : [
            {
              key,
              chip: (
                <ParamChip
                  words={`${label}: ${value}`}
                  body={null}
                  onPress={() => open(label)}
                  onRemove={onRemove}
                />
              )
            }
          ]
    );
  };
  const quickCount = quickChipsWith(openFilters).length;
  const appliedChipsWith = (open: (label: string) => true): ICompactChip[] => [
    ...(flags.length > 0 ? [{ key: 'flags', chip: flagChip }] : []),
    ...quickChipsWith(open)
  ];
  const appliedChips = appliedChipsWith(openFilters);
  const pageChips = appliedChipsWith(toolbar.openQuick);
  const viewItems = viewMenuItems(params, view, counted);
  const openView = viewItems.find((item) => item.current) ?? viewItems[0];

  return (
    <>
      <PageHeader
        title="Compliance queues"
        row
        tabs={<QueueTabs params={params} view={view} counts={counted} />}
        actions={
          <WorkPageHeaderActions
            summary={total === null ? null : `${total.toLocaleString('en-US')} ${total === 1 ? 'item' : 'items'}`}
            summaryData={{ 'data-queue-summary': '' }}
            refresh={refresh}
            help={help}
          />
        }
      />
      <CompactBar
        shown={scrolled}
        label="Compliance queues, compact"
        actions={<WorkPageHeaderActions summary={null} refresh={refresh} help={help} />}
        below={appliedChips.length > 0 && <CompactChipLine chips={appliedChips} onClearAll={clear} />}
      >
        <CompactTitle>Compliance queues</CompactTitle>
        <CompactMenu
          label="Queue"
          trigger={survey === null ? 'All' : asSpelled(survey)}
          items={queueItems(params, view, counted)}
        />
        <CompactMenu
          label="View"
          chip
          trigger={
            <>
              {openView.label}
              <span className="font-semibold tabular-nums">{openView.count}</span>
            </>
          }
          items={viewItems}
        />
        <CompactDivider />
        <CompactSearch {...searchBox} />
        <CompactFilters count={quickCount} open={filtersOpen} onOpenChange={setFiltersOpen}>
          {quickFilters}
        </CompactFilters>
        <span className="flex-1" />
        <CompactDivider />
        {assignAll}
        {columnsButton}
        <CompactDivider />
      </CompactBar>
      <div className="grid min-w-0 gap-3 px-6 py-4">
        {viewCards}

        <WorkToolbar
          toolbar={toolbar}
          search={
            <form
              role="search"
              className="w-full min-w-48 sm:w-auto sm:max-w-96 sm:flex-1"
              onSubmit={(event) => {
                event.preventDefault();
                searchBox.onSubmit();
              }}
            >
              <FilterBox
                label={searchBox.label}
                placeholder={searchBox.placeholder}
                value={searchBox.draft}
                onChange={searchBox.onDraft}
                className="block"
                inputClassName="h-9"
              />
            </form>
          }
          quick={quickFilters}
          quickCount={quickCount}
          end={
            <>
              <HelpPopover label={`About ${VIEW_LABEL[view]}`} align="end">
                <p>
                  {VIEW_LABEL[view]}: {VIEW_ABOUT[view]}
                </p>
                <p>
                  Raised by, Concern, Assigned to and the column funnels narrow the list; the cards
                  count under the same filters, the queue tabs under every one but the queue.
                  Concern keeps the items any of the chosen concern criteria raised.
                </p>
                <p>
                  Subject, Raised, Last modified and Due sort the list, one at a time; newest
                  raised first by default.
                </p>
                <p>
                  Latest note is the newest rationale written on an item — a decision’s, a
                  reopening’s, a new task’s note or an automatic escalation’s — with who wrote it
                  and when. Related to is the person the item flags.
                </p>
                <p>
                  The search finds every word in the advisor’s or team’s name or firm, the flagged
                  member’s name or a CRD, in every queue: the tabs count its hits.
                </p>
                <p>
                  Assign all gives every item in the list, on every page, to one colleague, or to
                  nobody — at most {ASSIGN_LIMIT.toLocaleString('en-US')} at once. Tick rows to assign only
                  those. Undo, beside the result, puts back whom each item had.
                </p>
              </HelpPopover>
              {assignAll}
              {columnsButton}
            </>
          }
        />
        {pageChips.length > 0 && <CompactChipLine label="Filters" chips={pageChips} onClearAll={clear} />}
        <div ref={setControlsEnd} aria-hidden data-controls-end="" className="-mt-3 h-0" />
        {assignMissing !== null && <NotPermitted id={assignReasonId} what={assignMissing} />}
        {assignedSaid && (
          <AssignedSaid
            key={assignedSaid.n}
            said={assignedSaid.said}
            onUndone={readAgain}
            onDismiss={() => setAssignedSaid(null)}
          />
        )}

        {error && shown !== null && (
          <LoadError message={`Could not refresh — showing the list as last read. ${error}`} />
        )}
        {counts.error && (
          <p role="status" className="text-sm text-muted-foreground">
            The queues are not counted: {counts.error}
          </p>
        )}
        {shown?.staff?.error && (
          <p role="status" className="text-sm text-muted-foreground">
            This queue shows no staff fields: {shown.staff.error}
          </p>
        )}
        {offered?.error && (
          <p role="status" className="text-sm text-muted-foreground">
            This queue shows no survey questions: {offered.error}
          </p>
        )}

        <BulkDecisions
          asked={asked}
          rows={selection.rows}
          onSelect={selection.onSelect}
          onDecided={readAgain}
          stickyTop={STICKY_BAR_TOP}
          extra={
            <SelectedAssign
              ids={selection.rows.map((row) => row.id)}
              assignees={assignees}
              assigneesUnread={assigneesUnread}
              missing={assignMissing}
              reasonId={assignReasonId}
              onAssigned={onAssigned}
            />
          }
        />

        <WorkItemsTable
          rows={shown?.page.items ?? []}
          state={tableState}
          search={location.search}
          itemPath={(id) => workItemPath(id)}
          queueColumn={survey === null}
          columns={chosen.resolve(own)}
          selected={selection.selected}
          onSelect={selection.onSelect}
          stickyTop={STICKY_TABLE_TOP}
        />

        {shown && (
          <WorkPager {...paging.pager} total={shown.page.total} busy={reading} sticky />
        )}
      </div>
    </>
  );
}

/** The design's four segments, as this page's `view` spells them (F9's R2) — and a fifth, `escalated`, the
 *  spec's secondary-review set (CMP-10 AC3, WF-05 step 7), worked by the final decision layer
 *  (SR-400's ruling 1: a view, not a person). */
type QueueView = 'outstanding' | 'parked' | 'escalated' | 'left_firm' | 'reviewed' | 'all';

const VIEW_LABEL: Record<QueueView, string> = {
  outstanding: 'Outstanding',
  parked: 'Need info or escalated',
  escalated: 'Escalated',
  left_firm: 'Left firm',
  reviewed: 'Reviewed',
  all: 'All'
};

/** The states a view stands for — `undefined` is every state. */
const VIEW_STATES: Record<QueueView, string[] | undefined> = {
  outstanding: ['open'],
  parked: ['pending_firm', 'escalated'],
  escalated: ['escalated'],
  left_firm: ['open', 'pending_firm', 'escalated'],
  reviewed: ['reviewed'],
  all: undefined
};

/** The Left firm view's `view` (SR-468) — and the `queue` of the tab it replaced. */
const LEFT_FIRM = 'left_firm';

/** The views the firm moves are kept out of — they wait in Left firm instead. */
const FIRM_MOVES_APART: readonly QueueView[] = ['outstanding', 'parked', 'escalated'];

/** Whether a view's list asks for the firm moves (`movedFirm`): only them, none, or either. */
function movedFirmOf(view: QueueView): boolean | undefined {
  if (view === 'left_firm') return true;
  return FIRM_MOVES_APART.includes(view) ? false : undefined;
}

/** The page's own parameter, so anything it does not know is the default — `pageOf`'s rule. */
function viewOf(raw: Nullable<string>): QueueView {
  return raw !== null && raw in VIEW_LABEL ? (raw as QueueView) : 'outstanding';
}

/** The URL's view — the Left firm tab's old address read as its view, until the page rewrites it. */
function viewOfParams(params: URLSearchParams): QueueView {
  return params.get('queue') === LEFT_FIRM ? 'left_firm' : viewOf(params.get('view'));
}

/** The URL's state as the list's query — one function for the list and its pane. */
export function complianceQueryOf(
  params: URLSearchParams,
  since: Nullable<string>,
  page = pageOf(params.get('page')),
  pageSize?: number
): IWorkItemQuery {
  const survey = params.get('queue') === LEFT_FIRM ? null : params.get('survey');
  const view = viewOfParams(params);
  const flag = flagsOf(params);
  return {
    survey: survey === null ? undefined : [survey],
    state: VIEW_STATES[view],
    // Sent as the URL spells them: an unknown one is survey-api's 400, by name. Left firm is the
    // firm moves alone, which no Raised by narrows.
    trigger: view === 'left_firm' ? [] : params.getAll('trigger'),
    movedFirm: movedFirmOf(view),
    criterion: params.getAll('criterion'),
    assignee: params.get('assignee') ?? undefined,
    sort: params.get('sort') ?? undefined,
    refreshRunId: params.get('run') || undefined,
    q: params.get('q') || undefined,
    flag,
    // `since` is what `new` reads, sent with it alone.
    ...(flag.includes('new') && since !== null && { since }),
    page,
    pageSize
  };
}

/** The flags the URL names, once each, as spelled — an unknown one survey-api's 400. */
function flagsOf(params: URLSearchParams): string[] {
  return [...new Set(params.getAll('flag').filter((flag) => flag !== ''))];
}

/** `name` without `value` — one repeated value dropped, the rest kept. */
function withoutValue(params: URLSearchParams, name: string, value: string) {
  const kept = params.getAll(name).filter((one) => one !== value);
  params.delete(name);
  for (const one of kept) params.append(name, one);
}

/** `params` turned to `view` — the default unwritten — dropping `trigger` on Left firm, which offers
 *  no Raised by to see it or clear it. */
function toView(params: URLSearchParams, view: string) {
  if (view === 'outstanding') params.delete('view');
  else params.set('view', view);
  if (view === LEFT_FIRM) params.delete('trigger');
}

/** A view's link: the rest of the query kept, from page 1. */
function viewParams(params: URLSearchParams, view: QueueView): URLSearchParams {
  const next = new URLSearchParams(params);
  toView(next, view);
  return dropPaging(next);
}

/** What the chip knows of the run: its read (`null` for no such run), or nothing — a reader
 *  without `integrations.configure`, whom survey-api refuses the run's route. */
type RunKnown = { kind: 'not-permitted' } | { kind: 'read'; run: Nullable<IRefreshRunDetail> };

/** The `run` filter's run as the page knows it — read once per id for an account holding
 *  `integrations.configure` — and what its chips say: until it is read, if the read fails and when
 *  no run has the id, the id as spelled, so the query asked is the one the chips show. Any other
 *  reader is not asked about a route that refuses it: "one run". */
interface IRunRead {
  known: Nullable<RunKnown>;
  error: Nullable<string>;
  created: Nullable<string>;
  summary: string;
}

function useRunRead(runId: Nullable<string>): IRunRead {
  // The refresh's routes are `integrations.configure`, its reads included (SR-228).
  const readsRuns = useCan()('integrations.configure');
  const { data, error } = useAdminData<Nullable<{ runId: string; known: RunKnown }>>(
    async (signal) =>
      runId === null
        ? null
        : {
            runId,
            known: readsRuns
              ? { kind: 'read', run: await brokerCheckRefreshApi.run(runId, signal) }
              : { kind: 'not-permitted' }
          },
    [readsRuns, runId]
  );
  // Another run's answer, while this one's is out, is not this run's.
  const known = data !== null && data.runId === runId ? data.known : null;
  const found = known?.kind === 'read' ? known.run : null;
  const created = found ? formatDateTime(found.createdAt) : null;
  const summary = created
    ? `the run of ${created}`
    : known?.kind === 'not-permitted'
      ? 'one run'
      : (runId ?? '');
  return { known, error, created, summary };
}

/**
 * The `run` filter (F10's Q3), a chip that names the run and removes the filter — over the page's
 * one read of the run (`useRunRead`), so the toolbar's chip, the compact bar's and the applied
 * filters' row say the same.
 */
function RunFilterChip({ runId, read, onRemove }: { runId: string; read: IRunRead; onRemove: () => void }) {
  const { known, error, created } = read;
  const found = known?.kind === 'read' ? known.run : null;

  return (
    <FilterChip label="Refresh run" summary={read.summary}>
      <p className="max-w-xs">
        {created
          ? `Only the work items the BrokerCheck refresh run of ${created} raised.`
          : 'Only the work items one BrokerCheck refresh run raised.'}
      </p>
      {known?.kind === 'not-permitted' && <NotPermitted what="the BrokerCheck refresh" />}
      {error && <p className="text-muted-foreground">The run could not be read: {error}</p>}
      {known?.kind === 'read' && known.run === null && (
        <p className="text-muted-foreground">No refresh run has this id.</p>
      )}
      <p className="text-xs text-muted-foreground">
        Run <span className="font-mono select-all">{runId}</span>
      </p>
      <div className="flex flex-wrap items-center gap-3">
        {found && (
          <Link
            to={refreshRunPath(found.id)}
            className="text-[var(--color-sapphire)] underline-offset-4 hover:underline"
          >
            Open the run
          </Link>
        )}
        <Button type="button" size="sm" variant="outline" onClick={onRemove}>
          Remove this filter
        </Button>
      </div>
    </FilterChip>
  );
}

/** The views in the cards' order. */
const VIEWS: readonly QueueView[] = ['outstanding', 'parked', 'escalated', 'left_firm', 'reviewed', 'all'];

/** What a view holds — the page's "?" and the toolbar's. */
const VIEW_ABOUT: Record<QueueView, string> = {
  outstanding: 'the open items, raised and not yet decided.',
  parked:
    'the items waiting on more information (Need additional info, until its follow-up day) or ' +
    'escalated.',
  escalated: 'the items escalated for the final decision.',
  left_firm:
    'the firm changes still to work — people who moved firm — decided Left firm or Approved; the ' +
    'three views before it leave them out.',
  reviewed: 'the items decided Approve or Remove; one can be reopened.',
  all: 'every item, in any state.'
};

/** A card's hint, in a few words. */
const VIEW_HINT: Record<QueueView, string> = {
  outstanding: 'review and decide',
  parked: 'waiting on info or escalated',
  escalated: 'for the final decision',
  left_firm: 'firm changes to work',
  reviewed: 'decided',
  all: 'every item'
};

/** The signals a card counts, in the order a compact card picks the first that holds any: each
 *  its count's name in the read and what it narrows the view by — a `flag`, or an `assignee`. */
const SIGNALS: readonly (SignalSpec & {
  counted: 'overdue' | 'dueToday' | 'new' | 'unassigned' | 'mine';
  narrows: ['flag' | 'assignee', string];
  /** The items it counts, in its button's name. */
  show: (items: string) => string;
})[] = [
  {
    key: 'overdue',
    label: 'overdue',
    tone: 'danger',
    counted: 'overdue',
    narrows: ['flag', 'overdue'],
    show: (items) => `overdue ${items}`
  },
  {
    key: 'due_today',
    label: 'due today',
    tone: 'attention',
    counted: 'dueToday',
    narrows: ['flag', 'due_today'],
    show: (items) => `${items} due today`
  },
  {
    key: 'new',
    label: 'new',
    tone: 'info',
    counted: 'new',
    narrows: ['flag', 'new'],
    show: (items) => `new ${items}`
  },
  {
    key: 'unassigned',
    label: 'unassigned',
    tone: 'info',
    counted: 'unassigned',
    narrows: ['assignee', 'none'],
    show: (items) => `unassigned ${items}`
  },
  {
    key: 'mine',
    label: 'mine',
    tone: 'info',
    counted: 'mine',
    narrows: ['assignee', 'me'],
    show: (items) => `${items} assigned to you`
  }
];

/** The signals of a due day, which a reviewed item no longer has. */
const DUE_SIGNALS: readonly string[] = ['overdue', 'due_today'];

/** A flag in the chip's words. */
const FLAG_WORDS: Record<string, string> = {
  overdue: 'overdue',
  due_today: 'due today',
  new: 'new since your last visit'
};

/** survey-api's four orders (`WorkItemSorts`), each in its one direction. */
type QueueSort = 'raised_at' | 'updated_at' | 'due_on' | 'subject';

const SORTS: Record<QueueSort, { label: string; direction: 'asc' | 'desc'; note: string }> = {
  subject: { label: 'Subject', direction: 'asc', note: 'Subject A to Z, newest raised first within one' },
  raised_at: { label: 'Raised', direction: 'desc', note: 'Newest raised first — the default order' },
  updated_at: { label: 'Last modified', direction: 'desc', note: 'Last modified first' },
  due_on: { label: 'Due', direction: 'asc', note: 'Due soonest first, reviewed items last' }
};

/** The states a view stands for, as the counts read keys them. */
const COUNTED_STATES = ['open', 'pending_firm', 'escalated', 'reviewed'] as const;

function statesOf(view: QueueView): readonly (typeof COUNTED_STATES)[number][] {
  return (VIEW_STATES[view] ?? COUNTED_STATES) as readonly (typeof COUNTED_STATES)[number][];
}

/** The counts read's query: the list's filters, less its state, flag, order, fields, page and
 *  `movedFirm` — one read counts every view — with the URL's own triggers (the Left firm view's
 *  list sends none), and `since`, which its `new` reads. */
function countsQueryOf(
  params: URLSearchParams,
  query: IWorkItemQuery,
  since: Nullable<string>
): IWorkItemCountsQuery {
  const { survey, criterion, assignee, participantId, refreshRunId, q } = query;
  return {
    survey,
    trigger: params.getAll('trigger'),
    criterion,
    assignee,
    participantId,
    refreshRunId,
    q,
    ...(since !== null && { since })
  };
}

/** The views as cards, each a link keeping the rest of the query and starting at page 1: its
 *  states' items under the filters, and their overdue, due today, new, unassigned and own as its
 *  signals — a count the read did not send left out, new before a first visit, and a due day's on
 *  Reviewed, whose items have none. Each signal is a toggle: the card's view narrowed by it too,
 *  the rest of the query kept — an assignee's in place of the other; pressed again, dropped. */
function ViewCards({
  params,
  view,
  counts
}: {
  params: URLSearchParams;
  view: QueueView;
  counts: Nullable<IWorkItemCounts>;
}) {
  const [, setParams] = useSearchParams();
  const { since } = useLastVisit();
  const total = (entry: QueueView, key: CountKey) =>
    counts ? viewCount(counts, entry, key, params) : null;
  const all = total('all', 'count');
  const read = (key: (typeof SIGNALS)[number]['counted']) =>
    Object.values(counts?.states ?? {}).some((state) => typeof state?.[key] === 'number');
  const shown = SIGNALS.filter((spec) => read(spec.counted) && (spec.key !== 'new' || since !== null));
  return (
    <StateCards
      label="View"
      compact
      cards={VIEWS.map((entry) => {
        const next = viewParams(params, entry);
        const count = total(entry, 'count');
        const possible = shown.filter((spec) => !(entry === 'reviewed' && DUE_SIGNALS.includes(spec.key)));
        // A signal survey-api did not count is not drawn.
        const signals: IStateSignal[] = possible.flatMap((spec) => {
          const count = total(entry, spec.counted);
          if (count === null) return [];
          const [name, value] = spec.narrows;
          const active = view === entry && params.getAll(name).includes(value);
          const items = count === 1 ? 'item' : 'items';
          return [
            {
              key: spec.key,
              label: spec.label,
              tone: spec.tone,
              count,
              active,
              toggleName: `Show ${count.toLocaleString('en-US')} ${spec.show(items)} in ${VIEW_LABEL[entry]}`,
              onToggle: () => {
                const narrowed = new URLSearchParams(next);
                if (active) withoutValue(narrowed, name, value);
                else if (name === 'flag') {
                  if (!narrowed.getAll(name).includes(value)) narrowed.append(name, value);
                } else narrowed.set(name, value);
                setParams(narrowed);
              }
            }
          ];
        });
        return {
          key: entry,
          to: { search: next.toString() },
          current: view === entry,
          label: VIEW_LABEL[entry],
          // A count survey-api did not send is no 0.
          count: count?.toLocaleString('en-US') ?? '…',
          hint: VIEW_HINT[entry],
          signals,
          share: count !== null && entry !== 'all' && all ? count / all : undefined,
          data: { 'data-view': entry },
          countData: { 'data-count': count?.toString() ?? '' }
        };
      })}
    />
  );
}

/** The queues as routed links: All, the mirror's, then any code only the counts or the URL
 *  names. Each keeps the rest of the query and starts at page 1. */
function queueItems(
  params: URLSearchParams,
  view: QueueView,
  counts: Nullable<IWorkItemCounts>
): ICompactMenuItem[] {
  const current = params.get('survey');
  const extra = [...Object.keys(counts?.bySurvey ?? {}), ...(current === null ? [] : [current])].filter(
    (code, index, all) => !isWorkItemQueue(code) && all.indexOf(code) === index
  );
  const tabs: Nullable<string>[] = [null, ...WORK_ITEM_QUEUES, ...extra];
  return tabs.map((code) => {
    const next = new URLSearchParams(params);
    if (code === null) next.delete('survey');
    else next.set('survey', code);
    dropPaging(next);
    return {
      key: code ?? '',
      label: code ?? 'All',
      to: { search: next.toString() },
      current: code === current,
      count: (counts && tabCount(counts, view, code, params)) ?? undefined
    };
  });
}

/** One count a state's items carry — the item count, or one of its signals. */
type CountKey = 'count' | (typeof SIGNALS)[number]['counted'];

/** Whether the firm moves are among the counted items — not where Raised by keeps only other
 *  triggers, which `states` and `bySurvey` then hold none of. */
function movesCounted(params: URLSearchParams): boolean {
  const triggers = params.getAll('trigger');
  return triggers.length === 0 || triggers.includes(FIRM_MOVE_TRIGGER);
}

/** A view's count from one part of the counts read — `table`, every item's, and `moves`, the firm
 *  moves' share of it, each summed by `sum`: Left firm's the firm moves alone, null where the read
 *  sent none; the three views they are kept out of `table`'s less the firm moves; Reviewed and All
 *  `table`'s whole. */
function lessFirmMoves<T>(
  view: QueueView,
  params: URLSearchParams,
  sum: (table: T | undefined) => number,
  table: T,
  moves: T | undefined
): Nullable<number> {
  if (view === 'left_firm') return moves ? sum(moves) : null;
  const all = sum(table);
  return FIRM_MOVES_APART.includes(view) && movesCounted(params) ? Math.max(0, all - sum(moves)) : all;
}

/** A view's count (or a signal's) from `states` and `firmMoves` (`lessFirmMoves`). */
function viewCount(
  counts: IWorkItemCounts,
  view: QueueView,
  key: CountKey,
  params: URLSearchParams
): Nullable<number> {
  const sum = (bucket: IWorkItemCounts['firmMoves']) =>
    statesOf(view).reduce((one, state) => one + (bucket?.[state]?.[key] ?? 0), 0);
  return lessFirmMoves(view, params, sum, counts.states, counts.firmMoves);
}

/** A queue tab's count in the view — All's with `code` null — from `bySurvey` and
 *  `firmMovesBySurvey` (`lessFirmMoves`). */
function tabCount(
  counts: IWorkItemCounts,
  view: QueueView,
  code: Nullable<string>,
  params: URLSearchParams
): Nullable<number> {
  const sum = (table: IWorkItemCounts['bySurvey'] | undefined) =>
    Object.entries(table ?? {})
      .filter(([survey]) => code === null || survey === code)
      .reduce(
        (total, [, byState]) => total + statesOf(view).reduce((one, state) => one + (byState[state] ?? 0), 0),
        0
      );
  return lessFirmMoves(view, params, sum, counts.bySurvey, counts.firmMovesBySurvey);
}

/** The views as the compact bar's menu — each card's link, words and count as `ViewCards` draws
 *  them: "…" until counted. */
function viewMenuItems(
  params: URLSearchParams,
  view: QueueView,
  counts: Nullable<IWorkItemCounts>
): ICompactMenuItem[] {
  return VIEWS.map((entry) => {
    const count = counts ? viewCount(counts, entry, 'count', params) : null;
    return {
      key: entry,
      label: VIEW_LABEL[entry],
      to: { search: viewParams(params, entry).toString() },
      current: view === entry,
      count: count?.toLocaleString('en-US') ?? '…'
    };
  });
}

/** The queues as tabs, each with its count in the view. */
function QueueTabs({
  params,
  view,
  counts
}: {
  params: URLSearchParams;
  view: QueueView;
  counts: Nullable<IWorkItemCounts>;
}) {
  return (
    <nav aria-label="Queues" className="-mx-1 flex flex-wrap gap-x-4">
      {queueItems(params, view, counts).map((tab) => (
        <Link
          key={tab.key}
          to={tab.to}
          aria-current={tab.current ? 'page' : undefined}
          className={cn(
            '-mb-px inline-flex items-baseline gap-1.5 border-b-2 px-1 pt-1 pb-2 text-[0.8125rem] transition-colors',
            tab.current
              ? 'border-[var(--color-teal)] font-medium text-[var(--color-royal-blue)]'
              : 'border-transparent text-muted-foreground hover:text-[var(--color-royal-blue)]'
          )}
        >
          {tab.label}{' '}
          {tab.count !== undefined && <span className="text-xs tabular-nums">{tab.count}</span>}
        </Link>
      ))}
    </nav>
  );
}

/** Whom an item can be assigned to, for the filter — Compliance's and Number checks'. Losing it
 *  leaves "Me" and "Unassigned", which need no read, rather than replacing the screen with an
 *  error it cannot act on. */
export function useAssignees(): { assignees: IWorkItemAssignee[]; assigneesUnread: boolean } {
  const { data, error } = useAdminData((signal) => workItemsApi.workItemAssignees(signal), []);
  return { assignees: data ?? [], assigneesUnread: error !== null };
}

/** The concern criteria the Concern filter offers — every code's current version, in the read's
 *  order, and its name by code — and whether the read answered: a reader it refuses is offered no
 *  filter. */
function useCriteria(): { codes: string[]; names: ReadonlyMap<string, string>; readable: boolean } {
  const { data } = useAdminData((signal) => workItemsApi.listConcernCriteria(signal), []);
  return React.useMemo(() => {
    const current = (data ?? []).filter((criterion) => criterion.supersededAt === null);
    return {
      codes: current.map((criterion) => criterion.code),
      names: new Map(current.map((criterion) => [criterion.code, criterion.name])),
      readable: data !== null
    };
  }, [data]);
}

/** The queue and an item, as one area: the list, or an item with the list beside it. */
export function AdminComplianceArea() {
  return (
    <ListArea
      param="workItemId"
      list={TEAM_PATHS.compliance}
      paneTitle="Compliance queues"
      pane={(search, selected) => <ComplianceListPane search={search} selected={selected} />}
    />
  );
}

/** The list as the pane beside an item draws it: the subject, whose and which queue, the state. */
function ComplianceListPane({ search, selected }: { search: string; selected: string }) {
  // The list's own Per page and window, so the URL's page is the same rows beside an item.
  const [perPage] = usePerPage();
  const { since } = useLastVisit();
  const { shown, pager, status } = usePaneRead(
    search,
    'No work item matches this view.',
    (params, page, signal) => {
      const window = windowOf(params, perPage);
      return readItemPages(
        (at, pageSize) => workItemsApi.listWorkItems(complianceQueryOf(params, since, at, pageSize), signal),
        page,
        perPage,
        page === window.page ? window.pages : 1,
        WORK_ITEMS_MAX_PAGE
      );
    }
  );
  return (
    <SplitList
      label="Work items"
      selected={selected}
      items={
        shown?.items.map((row) => ({
          key: row.id,
          to: workItemPath(row.id),
          state: { search, list: true } satisfies ICardLinkState,
          primary: row.subject,
          secondary: `${row.participant.displayName} · ${row.surveyCode}`,
          trailing: <WorkItemStateChip state={row.state} />
        })) ?? null
      }
      status={status}
      footer={pager}
    />
  );
}
